import { useQuery } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  NewspaperIcon,
  StickyNote,
  Users2Icon,
} from "lucide-react";
import { useState } from "react";
import { ListBox, ListBoxItem } from "react-aria-components";

import { FeedEmbed } from "@/components/FeedEmbed";
import JsonEditor from "@/components/JsonEditor";
import { Picture } from "@/components/Picture";
import { Avatar } from "@/components/ui/avatar";
import { ButtonLink } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Link } from "@/components/ui/typography";
import pinkFlower from "@/images/pink-flower.png?w=500;900;1200&format=avif;webp;jpg&as=picture";
import { getPublicFeedQueryOptions } from "@/lib/api";
import { FeedVersion } from "@/lib/types";
import { cn, formatBigNumber } from "@/lib/utils";

function EmbedExample() {
  const { data: feed } = useQuery(getPublicFeedQueryOptions(396));

  if (!feed) return null;

  return (
    <div className="lg:py-4 lg:px-8 h-full flex items-center justify-center flex-col gap-4">
      <div className="max-h-[600px] h-full w-full border border-slate-7 dark:border-slatedark-7 rounded-lg flex flex-col overflow-hidden bg-slate-app">
        <div className="flex items-center gap-4 p-4">
          <Avatar src={feed.avatar_url} alt="" className="w-10 h-10" />
          <div className="flex flex-col gap-1 flex-1">
            <div className="text-lg font-semibold">{feed.display_name}</div>
            <div className="text-sm text-slate-dim">@aendra.com</div>
          </div>

          <div className="flex gap-2 md:gap-4 items-center flex-wrap">
            <SimpleTooltip message="Average Unique Users">
              <div className="flex gap-1 items-center text-slate-dim text-sm">
                <Users2Icon className="h-4 w-4" />
                {formatBigNumber(feed.stats.summary.avg_unique_user_count)}
              </div>
            </SimpleTooltip>
            <SimpleTooltip message="Average Post Views">
              <div className="flex gap-1 items-center text-slate-dim text-sm">
                <StickyNote className="h-4 w-4" />
                {formatBigNumber(feed.stats.summary.avg_post_view_count)}
              </div>
            </SimpleTooltip>
            <SimpleTooltip message="Average Feed Views">
              <div className="flex gap-1 items-center text-slate-dim text-sm">
                <NewspaperIcon className="h-4 w-4" />
                {formatBigNumber(feed.stats.summary.avg_feed_view_count)}
              </div>
            </SimpleTooltip>
          </div>
        </div>
        <Separator />
        <FeedEmbed feedId="396" fullscreen={false} className="min-h-[300px]" />
      </div>
      <Link
        to="/docs/$slug"
        params={{ slug: "embed-feeds" }}
        className="text-sm text-slate-dim flex items-center gap-1"
      >
        Learn more
        <ArrowRightIcon className="w-4 h-4" />
      </Link>
    </div>
  );
}

function CustomNodesExample() {
  return (
    <div className="lg:py-4 lg:px-8 pt-0 h-full flex flex-col gap-8 items-center justify-center">
      <div className="flex flex-col">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4 p-4">
            <Avatar
              src="https://cdn.bsky.app/img/avatar/plain/did:plc:6i6n57nrkq6xavqbdo6bvkqr/bafkreif4jlf3rth75f5eglob6r5rnbxlh3ncx3i2lfnm6zhk7unpfq6hra@jpeg"
              alt=""
              className="w-10 h-10"
            />
            <div className="flex flex-col gap-1 flex-1">
              <div className="text-lg font-semibold">Bots Begone</div>
              <div className="text-sm text-slate-dim">From @byarielm.fyi</div>
            </div>
          </div>
          <ButtonLink
            to="/custom-nodes/$nodeId"
            params={{ nodeId: "680" }}
            variant="ghost"
          >
            View
            <ArrowRightIcon className="w-4 h-4" />
          </ButtonLink>
        </div>
        <div className="flex flex-col rounded-lg border-2 bg-slate-6 dark:bg-slatedark-6 border-slate-8 hover:border-slate-9 dark:border-slatedark-8 dark:hover:border-slatedark-9">
          <div className="bg-slate-7 dark:bg-slatedark-7 text-slate-normal border-slate-8 dark:border-slatedark-8 text-xs font-mono uppercase pt-[5px] pb-[5px] h-8 px-3 border-b flex gap-2 items-center justify-between rounded-t-[7px]">
            <span className="flex items-center flex-1 min-w-0 gap-2">
              Custom Node
            </span>
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="pointer-events-auto opacity-30"
              data-state="closed"
              data-tooltip-trigger="true"
            >
              <path
                d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z"
                fill="currentColor"
                fillRule="evenodd"
                clipRule="evenodd"
              ></path>
            </svg>
          </div>
          <div className="flex flex-col">
            <div className="px-3.5 py-4 flex flex-col gap-2 group pointer-events-auto sort-area-block">
              <div className="flex flex-col rounded-lg border-2 bg-amber-6 dark:bg-amberdark-6 border-amber-8 hover:border-amber-11 dark:border-amberdark-8 dark:hover:border-amberdark-11">
                <div className="bg-amber-7 dark:bg-amberdark-7 text-amber-normal border-amber-8 dark:border-amberdark-8 text-xs font-mono uppercase pt-[5px] pb-[5px] h-8 px-3 border-b flex gap-2 items-center justify-between rounded-t-[7px]">
                  <span className="flex items-center flex-1 min-w-0 gap-2">
                    Parameters
                  </span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="pointer-events-auto opacity-30"
                    data-state="closed"
                    data-tooltip-trigger="true"
                  >
                    <path
                      d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z"
                      fill="currentColor"
                      fillRule="evenodd"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
                <div className="flex flex-col gap-4 p-3 pt-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <div className="text-lg font-medium flex-1 flex items-center gap-3">
                              <span className="flex-1">
                                Alert Bots (Transit, Weather, etc.)
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 text-slate-dim">
                            hand curated list of bots that post transit,
                            weather, aviation, and shipping updates. Double
                            check this list if you're creating a
                            location-specific feed! You may or may not want
                            *some* of these bots in the feed.
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="py-1 text-sm">Options:</div>
                            <div className="flex flex-wrap gap-2">
                              <div className="bg-amber-3 dark:bg-amberdark-3 border-amber-8 dark:border-amberdark-8 text-amber-normal text-sm px-2 py-1 border rounded-md break-all min-w-0 w-fit">
                                Block
                              </div>
                              <div className="bg-amber-3 dark:bg-amberdark-3 border-amber-8 dark:border-amberdark-8 text-amber-normal text-sm px-2 py-1 border rounded-md break-all min-w-0 w-fit">
                                Do Not Block
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        data-orientation="horizontal"
                        role="none"
                        className="shrink-0 h-px w-full bg-amber-8 dark:bg-amberdark-8 my-2 hidden lg:block"
                      ></div>
                      <div className="flex-col gap-2 hidden lg:flex">
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <div className="text-lg font-medium flex-1 flex items-center gap-3">
                              <span className="flex-1">
                                Social Media Mirror Bots
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 text-slate-dim">
                            hand curated list of bots that mirror content other
                            social media sites (twitter, reddit, etc.) to
                            bluesky. assorted topics - these accounts won't be
                            organized into topical bot lists.
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="py-1 text-sm">Options:</div>
                            <div className="flex flex-wrap gap-2">
                              <div className="bg-amber-3 dark:bg-amberdark-3 border-amber-8 dark:border-amberdark-8 text-amber-normal text-sm px-2 py-1 border rounded-md break-all min-w-0 w-fit">
                                Block
                              </div>
                              <div className="bg-amber-3 dark:bg-amberdark-3 border-amber-8 dark:border-amberdark-8 text-amber-normal text-sm px-2 py-1 border rounded-md break-all min-w-0 w-fit">
                                Do Not Block
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Link
        to="/docs/$slug"
        params={{ slug: "custom-nodes" }}
        className="text-sm text-slate-dim flex items-center gap-1"
      >
        Learn more
        <ArrowRightIcon className="w-4 h-4" />
      </Link>
    </div>
  );
}

function PrivateFeedsExample() {
  return (
    <div className="p-4 lg:px-8 flex h-full items-center justify-center flex-col gap-6">
      <h3 className="text-2xl md:text-3xl font-semibold">
        Give Perks to Your Subscribers
      </h3>
      <div className="flex flex-col gap-4 items-center">
        <div className="relative">
          <div className="flex flex-col border border-slate-7 dark:border-slatedark-7 rounded-lg overflow-hidden shadow-md bg-slate-app">
            <div className="flex flex-col py-4 gap-3">
              <div className="pl-4 pr-2 flex gap-3 items-start justify-between">
                <div className="flex gap-3 items-center flex-1 min-w-0">
                  <span className="relative flex shrink-0 overflow-hidden rounded-full border border-slate-dim w-9 h-9">
                    <img
                      className="aspect-square h-full w-full"
                      alt="#BookSky moderator 💙📚"
                      src="https://images.nature.com/lw1200/magazine-assets/d41586-024-02191-1/d41586-024-02191-1_27293496.jpg"
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-normal font-medium truncate">
                      Space News Trending 🚀
                    </div>
                    <div className="text-xs text-slate-dim-overridable truncate">
                      @space.news
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="text-xs text-slate-dim-overridable px-2">
                    3 months
                  </div>
                </div>
              </div>
              <div className="px-4 flex flex-col gap-3">
                <div className="p-1">
                  <div className="break-words text-sm whitespace-pre-wrap">
                    Why join the{" "}
                    <a
                      className="font-medium text-blue-11 dark:text-bluedark-11 underline underline-offset-4"
                      href="https://patreon.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Space News Patreon?
                    </a>{" "}
                    <br />
                    <br />⭐ Enjoy all of our feeds ad-free
                    <br />⭐ Get access to our members-only feeds
                    <br />⭐ You want to directly support our work
                  </div>
                </div>
              </div>
              <div className="flex gap-3 items-center px-4 text-slate-dim-overridable text-xs">
                <div className="flex gap-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-message-circle h-3.5 w-3.5"
                  >
                    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                  </svg>
                  <div>1</div>
                </div>
                <div className="flex gap-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-repeat2 h-3.5 w-3.5"
                  >
                    <path d="m2 9 3-3 3 3"></path>
                    <path d="M13 18H7a2 2 0 0 1-2-2V6"></path>
                    <path d="m22 15-3 3-3-3"></path>
                    <path d="M11 6h6a2 2 0 0 1 2 2v10"></path>
                  </svg>
                  <div>12</div>
                </div>
                <div className="flex gap-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-heart h-3.5 w-3.5"
                  >
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                  </svg>
                  <div>50</div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-3 right-4 flex gap-2 items-center"></div>
        </div>
        <Link
          to="/docs/$slug"
          params={{ slug: "private-feeds" }}
          className="text-sm text-slate-dim flex items-center gap-1"
        >
          Learn more
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

interface FeatureData {
  id: string;
  description: string;
  example: React.ComponentType;
}

const features: FeatureData[] = [
  {
    id: "Powerful Editor",
    description: "Easily build feeds with a visual editor and powerful logic",
    example: () => (
      <div className="lg:py-4 lg:px-8 flex items-center justify-center h-full">
        <div className="flex flex-col gap-4 items-center">
          <div className="rounded-lg border border-slate-7 dark:border-slatedark-7 overflow-hidden flex-1 flex flex-col">
            <video
              src="/editor.mp4"
              className="w-full h-full"
              autoPlay
              muted
              loop
            />
          </div>
          <Link
            to="/docs/$slug"
            params={{ slug: "creating-your-first-feed" }}
            className="text-sm text-slate-dim flex items-center gap-1"
          >
            Learn more
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    ),
  },
  {
    id: "Custom Nodes",
    description: "Build feed logic once, reuse everywhere",
    example: () => <CustomNodesExample />,
  },
  {
    id: "Contrails",
    description: "Real-time filtered streams for developers",
    example: () => (
      <div className="flex flex-col h-full items-center justify-center gap-4 lg:py-4 lg:px-8">
        <div className="flex flex-col gap-2">
          <p className="text-slate-dim text-lg text-center">
            Build apps on top of your feed with our Contrails API.
            <br />
          </p>
          <p className="text-slate-dim text-lg text-center">
            Simply open up a websocket connection to your feed and start
            receiving filtered posts in real-time.
          </p>
        </div>
        <div className="font-mono text-sm border-2 border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden px-4 py-3 bg-slate-12 text-slate-contrast text-slate-dim">
          <span className="text-green-8">❯</span> websocat
          "wss://api.graze.social/app/contrail?feed=YOUR_FEED_URL"
        </div>
        <div className="border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden">
          <JsonEditor
            feedVersion={
              {
                kind: "commit",
                did: "did:plc:l3sth2ayfujvmi7bp57snkrt",
                commit: {
                  cid: "bafyreicurcbsehrgbf473lo5pxvcpyj4og5a4fcglmj2y3eyapnvyeup54",
                  collection: "app.bsky.feed.post",
                  operation: "create",
                  record: {
                    $type: "app.bsky.feed.post",
                    createdAt: "2025-09-21T08:51:42.000Z",
                    langs: ["en"],
                    text: "Watch Steph Curry react to Carlos Alcaraz's tweener at the 2025 Laver Cup. The Team World v Team Europe event is live on TNT Sports and discovery\ufe63 [\u2026]",
                  },
                },
              } as unknown as FeedVersion
            }
          />
        </div>
        <Link
          to="/docs/$slug"
          params={{ slug: "contrails" }}
          className="text-sm text-slate-dim flex items-center gap-1"
        >
          Learn more
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    ),
  },
  {
    id: "Private Feeds",
    description: "Subscriber-only content spaces with Patreon integration",
    example: () => <PrivateFeedsExample />,
  },
  {
    id: "People-first Advertising",
    description:
      "Operators can connect directly with advertisers to discuss campaigns on Graze",
    example: () => (
      <div className="lg:py-4 lg:px-8 flex h-full items-center justify-center flex-col gap-6">
        <h3 className="text-2xl md:text-3xl font-semibold">
          Incoming Campaign Request
        </h3>
        <div className="max-w-xl w-full flex flex-col bg-slate-subtle rounded-lg border border-slate-6 dark:border-slatedark-6">
          <div className="p-4 border-b border-slate-6 dark:border-slatedark-6">
            <div className="flex items-center justify-between gap-8 self-stretch">
              <div className="flex flex-col gap-3 self-stretch">
                <div className="flex items-center gap-4">
                  <span className="relative flex shrink-0 overflow-hidden border border-slate-dim rounded-lg h-[52px] w-[52px]">
                    <img
                      className="aspect-square h-full w-full"
                      alt="Web Development Trending's avatar"
                      src="https://images.nature.com/lw1200/magazine-assets/d41586-024-02191-1/d41586-024-02191-1_27293496.jpg"
                    />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <h1 className="text-2xl font-semibold">Conference Promo</h1>
                    <div className="text-sm text-slate-dim">
                      Space News Trending
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 border-b border-slate-6 dark:border-slatedark-6 p-4">
            <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
              <div className="md:text-xl font-semibold">$0.50</div>
              <div className="text-slate-dim text-xs">CPM</div>
            </div>
            <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
              <div className="md:text-xl font-semibold">10000</div>
              <div className="text-slate-dim text-xs">Impressions</div>
            </div>
            <div className="flex flex-col items-start gap-2 bg-slate-3 dark:bg-slatedark-2 rounded-lg p-4">
              <div className="md:text-xl font-semibold">$5.00</div>
              <div className="text-slate-dim text-xs">Order</div>
            </div>
          </div>
          <div className="max-h-[80vh] overflow-auto border-b border-slate-6 dark:border-slatedark-6 p-4">
            <div className="relative">
              <div className="flex flex-col border border-slate-7 dark:border-slatedark-7 rounded-lg overflow-hidden shadow-md bg-slate-app">
                <div className="flex flex-col py-4 gap-3">
                  <div className="pl-4 pr-2 flex gap-3 items-start justify-between">
                    <div className="flex gap-3 items-center flex-1 min-w-0">
                      <span className="relative flex shrink-0 overflow-hidden rounded-full border border-slate-dim w-9 h-9">
                        <img
                          className="aspect-square h-full w-full"
                          alt="Nick Gerakines"
                          src="https://cdna.artstation.com/p/assets/images/images/027/349/840/large/dimitar-bochukov-ineedspace-avatar.jpg?1591280598"
                        />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-normal font-medium truncate">
                          SpaceConf
                        </div>
                        <div className="text-xs text-slate-dim-overridable truncate">
                          @spaceconf.us
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 flex flex-col gap-3">
                    <div className="p-1">
                      <div className="text-sm flex flex-col gap-2">
                        <div>Tickets are on sale now!</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slatea-2 dark:bg-slatedarka-2 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </div>
          <div className="flex justify-end gap-4 p-4">
            <button
              type="button"
              className="inline-flex shrink-0 relative items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-[disabled]:pointer-events-none aria-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 aria-expanded:bg-slatea-action border border-slate-normal bg-slate-1 dark:bg-slatedark-1 hover:bg-slate-4 dark:hover:bg-slatedark-4 active:bg-slate-5 dark:active:bg-slatedark-5 h-10 px-2 md:px-4 py-2 flex-1"
              aria-haspopup="dialog"
              aria-expanded="false"
              aria-controls="radix-«r1»"
              data-state="closed"
            >
              <span className="inline-flex items-center gap-2">Reject</span>
            </button>
            <button
              type="button"
              className="inline-flex shrink-0 relative items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-[disabled]:pointer-events-none aria-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 aria-expanded:bg-slatea-action bg-slate-12 dark:bg-slatedark-12 text-slate-1 dark:text-slatedark-1 hover:bg-slate-11 dark:hover:bg-slatedark-11 h-10 px-2 md:px-4 py-2 flex-1"
            >
              <span className="inline-flex items-center gap-2">Accept</span>
            </button>
          </div>
        </div>
        <Link
          to="/docs/$slug"
          params={{ slug: "sponsored-posts" }}
          className="text-sm text-slate-dim flex items-center gap-1"
        >
          Learn more
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    ),
  },
  {
    id: "Feed Embeds",
    description: "Drop your feed into any website with one line of code",
    example: () => <EmbedExample />,
  },
];

export function FeatureSection() {
  const [selectedFeatureId, setSelectedFeatureId] = useState(features[0]!.id);
  const selectedFeature = features.find((f) => f.id === selectedFeatureId);
  const ActiveFeatureExample = selectedFeature?.example;

  return (
    <section className="py-6 md:py-16 relative z-0">
      <Picture
        className="w-[15vw] absolute top-8 right-4 z-[-1]"
        alt="Features"
        src={pinkFlower}
      />

      <div className="container mx-auto px-4 md:px-12 py-4 flex flex-col gap-6 md:gap-10">
        <h2 className="text-4xl md:text-6xl font-bold instrument-serif">
          Features Curators Love
        </h2>
        <div className="flex flex-col lg:flex-row gap-8">
          <ListBox
            aria-label="Features"
            className="grid grid-cols-2 lg:flex lg:flex-col gap-3 flex-1"
            items={features}
            selectionMode="single"
            selectedKeys={[selectedFeatureId]}
            onSelectionChange={(key) => {
              if (!key || key === "all") return;
              const selected = [...key][0];
              if (!selected) return;
              setSelectedFeatureId(selected as string);
            }}
          >
            {(item) => (
              <ListBoxItem
                textValue={item.id}
                className={cn(
                  "flex flex-col gap-1.5 bg-orange-action text-left rounded-lg px-5 py-4 w-full",
                  "border-2 border-orange-dim",
                  "aria-selected:bg-orange-10 dark:aria-selected:bg-orangedark-10 aria-selected:text-orange-contrast aria-selected:border-orange-normal",
                )}
                onHoverChange={(isHovered) => {
                  if (isHovered) {
                    setSelectedFeatureId(item.id);
                  }
                }}
              >
                <h3 className="text-lg lg:text-2xl font-bold instrument-serif">
                  {item.id}
                </h3>
                <p className="md:text-lg hidden lg:block">{item.description}</p>
              </ListBoxItem>
            )}
          </ListBox>
          <div className="text-lg md:text-xl lg:hidden text-center text-slate-dim">
            {selectedFeature?.description}
          </div>
          <Separator className="lg:hidden" />
          <div className="flex-1">
            {ActiveFeatureExample && <ActiveFeatureExample />}
          </div>
        </div>
      </div>
    </section>
  );
}
